import { useLocation } from 'react-router-dom'

import {
  Button,
  ChevronDownIcon,
  DownloadIcon,
  Flex,
  PageHeader,
  PageLayout,
  Select,
  ToggleGroup,
  useBreakpoint,
} from '@buffer-mono/popcorn'

import { FilterByChannel } from '~publish/components/FilterByChannel'
import { FilterByTag } from '~publish/components/FilterByTag'

import { InsightsNavigationTabs } from '../../components'
import { DATE_RANGE_OPTIONS } from '../../utils'

import { BlankMetricsChart } from './BlankMetricsChart'
import { BlankSummaryMetrics } from './BlankSummaryMetrics'
import { BlankTopPosts } from './BlankTopPosts'
import { ConnectChannelBanner } from './ConnectChannelBanner'
import { InsightsEmptyState } from './InsightsEmptyState'

import styles from './AllChannelsInsightsEmptyState.module.css'

import type { JSX } from 'react'

type AllChannelsInsightsEmptyStateProps = {
  /**
   * Hide the connect-channel hero banner. Set when the org *has* channels but
   * the actor can access none of them: the page chrome and blank teaser
   * sections still render, but the connect CTA is suppressed because the actor
   * isn't the one who'd connect/own channels (CORE-6008). Defaults to `false`
   * (brand-new org with no channels — the connect CTA is the whole point).
   */
  hideConnectCta?: boolean
}

/**
 * Fresh-start All Channels Insights view, shown to users whose organization
 * has no channels connected. A connect-channel banner sits above blank "teaser"
 * sections that preview what Insights will look like once data flows in.
 *
 * Rendered by {@link InsightsPageOrSingleChannelRedirect} in place of the
 * data-driven {@link AllChannelsInsightsPage} when there are no accessible
 * channels. With `hideConnectCta` the hero is dropped (org has channels the
 * actor can't access); without it the hero invites connecting a channel.
 */
export function AllChannelsInsightsEmptyState({
  hideConnectCta = false,
}: AllChannelsInsightsEmptyStateProps = {}): JSX.Element {
  const { search } = useLocation()
  const breakpoint = useBreakpoint()
  const useSelectLayout = breakpoint !== 'lg' && breakpoint !== 'xl'

  return (
    <InsightsEmptyState>
      <InsightsEmptyState.Header>
        <InsightsNavigationTabs
          activeTab="post-insights"
          postInsightsHref={`/insights${search}`}
        />
      </InsightsEmptyState.Header>
      <PageLayout.Container>
        <PageHeader.Row className={styles.toolbarRow}>
          <Flex align="center" justify="between" gap="md" fullWidth>
            {useSelectLayout ? (
              <Select
                value="last-7-days"
                onValueChange={(): void => {}}
                disabled
              >
                <Select.Trigger size="medium" />
                <Select.Content>
                  {DATE_RANGE_OPTIONS.map((option) => (
                    <Select.Item key={option.value} value={option.value}>
                      {option.label}
                    </Select.Item>
                  ))}
                  <Select.Item value="custom">Custom</Select.Item>
                </Select.Content>
              </Select>
            ) : (
              <ToggleGroup defaultValue="last-7-days" disabled>
                {DATE_RANGE_OPTIONS.map((option) => (
                  <ToggleGroup.Item key={option.value} value={option.value}>
                    {option.label}
                  </ToggleGroup.Item>
                ))}
                <ToggleGroup.Item value="custom">Custom</ToggleGroup.Item>
              </ToggleGroup>
            )}
            <div className={styles.filterActions}>
              <FilterByChannel
                channels={[]}
                value={[]}
                onSelect={(): void => {}}
              />
              <FilterByTag value={[]} onSelect={(): void => {}} />
              <span className={styles.disabledControl}>
                <Button variant="tertiary" size="medium" disabled>
                  <DownloadIcon />
                  Export
                  <ChevronDownIcon />
                </Button>
              </span>
            </div>
          </Flex>
        </PageHeader.Row>
      </PageLayout.Container>
      <PageLayout.Container className={styles.contentContainer}>
        <Flex direction="column" gap="lg">
          {!hideConnectCta && <ConnectChannelBanner />}
          <BlankSummaryMetrics />
          <BlankTopPosts />
          <BlankMetricsChart />
        </Flex>
      </PageLayout.Container>
    </InsightsEmptyState>
  )
}
